import { logger as SentryLogger } from '@/shared/helpers/logger';
import {
  setLoaded,
  setLoading,
  setOrganizationData,
} from '@/viewmodel/features/user/state/organizationSlice';
import type { OrganizationData } from '@/viewmodel/features/user/state/organizationTypes';
import { useLogto } from '@logto/react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

export const useOrganizationData = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, fetchUserInfo, isLoading: isLogtoLoading } = useLogto();

  const initializationRef = useRef<'idle' | 'fetching' | 'done'>('idle');
  const prevAuthRef = useRef<boolean | null>(null);
  const fetchUserInfoRef = useRef(fetchUserInfo);

  useEffect(() => {
    fetchUserInfoRef.current = fetchUserInfo;
  });

  const handleLogout = useCallback(() => {
    initializationRef.current = 'idle';
    dispatch(setOrganizationData(null as unknown as OrganizationData));
    dispatch(setLoading(false));
    dispatch(setLoaded(false));
  }, [dispatch]);

  useEffect(() => {
    if (isLogtoLoading) {
      return;
    }

    if (!isAuthenticated) {
      if (prevAuthRef.current === true) {
        handleLogout();
      }
      prevAuthRef.current = isAuthenticated;
      return;
    }

    prevAuthRef.current = isAuthenticated;

    if (initializationRef.current !== 'idle') {
      return;
    }

    initializationRef.current = 'fetching';
    dispatch(setLoading(true));

    (async () => {
      try {
        const userInfo = await fetchUserInfoRef.current();

        if (
          userInfo?.organization_data &&
          Array.isArray(userInfo.organization_data) &&
          userInfo.organization_data.length > 0
        ) {
          const firstOrg = userInfo.organization_data[0] as OrganizationData;
          const orgData: OrganizationData = {
            id: firstOrg.id as string,
            name: firstOrg.name as string,
          };
          dispatch(setOrganizationData(orgData));
        }

        dispatch(setLoaded(true));
      } catch (error) {
        SentryLogger.error('Error fetching organization data:', {
          error: error as Error,
          log_source: 'useOrganizationData',
        });
        dispatch(setLoaded(true));
      } finally {
        dispatch(setLoading(false));
        initializationRef.current = 'done';
      }
    })();
  }, [isAuthenticated, isLogtoLoading, dispatch, handleLogout]);
};
